import { Injectable, Optional } from '@angular/core';
import { WorkspaceApiManagerService } from '../api/managers/workspace-api-manager.service';
import { OrganizationStateManagerService } from '../../organization/state-management/organization-state-manager.service';
import { BehaviorSubject, Subject, firstValueFrom } from 'rxjs';
import {
    IWorkspace,
    IWSInDTO,
    WSAssocUpdateActionType,
    WSUpdateAction,
    WSUpdateAssocEnum,
} from '../../../models/workspace/workspace-model';
import { MissingArgumentsError } from '../../../models/errors/general.errors';
import { WorkspaceConverterService } from '../converters/workspace-converter.service';
import { IOrganIn } from '../../../models/organization/organization-model';
import { FunctionsHelperService } from '../../functions-helper.service';
import { IBranding } from '../../../models/user';
import { OrganizationApiService } from '../../organization/api/organization-api.service';
import { SnackBarService } from '../../utils/snack-bar.service';

@Injectable()
export class WorkspaceStateManagerService {
    private _currentWorkspaceSubject = new BehaviorSubject<{ workspace: IWorkspace; isSwapped: boolean }>({
        workspace: null,
        isSwapped: false,
    });
    public currentWorkspace$ = this._currentWorkspaceSubject.asObservable();

    private _isMasterBrandingSubject = new BehaviorSubject<boolean>(false);
    public isMasterBranding$ = this._isMasterBrandingSubject.asObservable();

    private _newWorkspaceCreatedSubject = new Subject<IWorkspace>();
    public newWorkspaceCreated$ = this._newWorkspaceCreatedSubject.asObservable();

    private _currentWorkspaceIdSubject = new BehaviorSubject<string>(null);
    public currentWorkspaceId$ = this._currentWorkspaceIdSubject.asObservable();

    private WORKSPACE_LS_ID = 'workspaceId';
    currentOrganId: string;
    private selectionSequence = 0;
    private pendingSelectionSequence: number | null = null;
    private candidateWorkspaceIds: string[] = [];
    private selectionErrorSubject = new BehaviorSubject<string>('');
    public selectionError$ = this.selectionErrorSubject.asObservable();

    constructor(
        private workspaceApiManagerService: WorkspaceApiManagerService,
        private organizationStateManager: OrganizationStateManagerService,
        private workspaceConverter: WorkspaceConverterService,
        private functionsHelper: FunctionsHelperService,
        private snackBarService: SnackBarService,
        @Optional() private organizationApi: OrganizationApiService = null
    ) {
        this.organizationStateManager.currentOrganization$.subscribe(async (organData) => {
            if (!organData || !organData.organization) {
                this.currentOrganId = null;
                this.clearWorkspace();
                return;
            }
            const { organization } = organData;
            const sameOrganization = this.currentOrganId === organization.id;
            const activeId = sameOrganization ? this._currentWorkspaceIdSubject.value : null;
            this.currentOrganId = organization.id;
            this.candidateWorkspaceIds = (organization.workspaces ?? []).map(ws => ws.id);
            if (sameOrganization && this.pendingSelectionSequence === this.selectionSequence) return;
            if (!sameOrganization) this.clearWorkspace();
            this.workspaceApiManagerService.setOrgContext({ organizationId: organization.id });
            const savedId = activeId || organization.workspaces?.find(ws => ws.isDefaultForUser)?.id || this.getWorkspaceIdFromLS();
            if (savedId) await this.selectWorkspaceAsync(savedId);
            else await this.recoverWorkspaceAsync(++this.selectionSequence, organization.id, new Set());
        });
        this.organizationStateManager.wsMetadataUpdated$.subscribe((newMetadata) => {
            if (!newMetadata) {
                return;
            }
            const workspaceData = this._currentWorkspaceSubject.value;
            const workspace = workspaceData?.workspace;
            if (!workspaceData || newMetadata.id !== workspace?.id) {
                return;
            }
            workspace.name = newMetadata.name;
            workspace.projectCount = newMetadata.projectCount;
            workspace.assetCount = newMetadata.assetCount;
            workspace.userCount = newMetadata.userCount;
            workspace.createdByEmail = newMetadata.createdByEmail;
            this.setCurrentWorkspace(workspace);
        });
    }

    public async selectWorkspaceAsync(wsId: string) {
        if (!wsId || !this.currentOrganId) return;
        const sequence = ++this.selectionSequence;
        const organizationId = this.currentOrganId;
        this.pendingSelectionSequence = sequence;
        try {
            this.selectionErrorSubject.next('');
            const { data, error } = await this.getWorkspaceAsync(wsId);
            if (!this.selectionIsCurrent(sequence, organizationId)) return;
            if (error || !data) {
                if (this.isUnavailable(error)) {
                    this.clearSelectedWorkspace();
                    await this.recoverWorkspaceAsync(sequence, organizationId, new Set([wsId]));
                } else this.selectionErrorSubject.next('Workspace could not be loaded. Try again.');
                return;
            }
            this.convertAndSetCurrentWS(data);
            await this.updateOrganWSDefaultIdsAsync();
        } finally {
            if (this.pendingSelectionSequence === sequence) this.pendingSelectionSequence = null;
        }
    }
    private selectionIsCurrent(sequence: number, organizationId: string) {
        return sequence === this.selectionSequence && organizationId === this.currentOrganId;
    }
    private isUnavailable(error: unknown) {
        if (!error || typeof error !== 'object' || !('status' in error)) return false;
        if (error.status === 404 || error.status === 403) return true;
        // Legacy workspace membership verification returns this specific 422 envelope.
        return error.status === 422 && 'error' in error && !!error.error && typeof error.error === 'object' &&
            'errorCode' in error.error && error.error.errorCode === 'UserNotFoundInWorkspaceError';
    }
    private async recoverWorkspaceAsync(sequence: number, organizationId: string, attempted: Set<string>) {
        const current = () => this.selectionIsCurrent(sequence, organizationId);
        const tryCandidates = async (ids: string[]) => {
            for (const id of ids) {
                if (!current()) return true;
                if (!id || attempted.has(id)) continue;
                attempted.add(id);
                const {data, error} = await this.getWorkspaceAsync(id);
                if (!current()) return true;
                if (data && !error) {
                    this.selectionErrorSubject.next(''); this.convertAndSetCurrentWS(data);
                    await this.updateOrganWSDefaultIdsAsync(); return true;
                }
                if (!this.isUnavailable(error)) { this.selectionErrorSubject.next('Workspace could not be loaded. Try again.'); return true; }
            }
            return false;
        };
        this.selectionErrorSubject.next('');
        if (await tryCandidates(this.candidateWorkspaceIds)) return;
        if (this.organizationApi && current()) {
            let page = 1;
            try {
                while (current()) {
                    const result = await firstValueFrom(this.organizationApi.getWorkspacesPaginated$({organizationId}, {page: String(page), limit: '25'}));
                    if (!current()) return;
                    if (await tryCandidates(result.workspaces.map(ws => ws.id))) return;
                    if (page >= result.pagination.totalPages || !result.workspaces.length) break;
                    ++page;
                }
            } catch (error) {
                if (current()) this.selectionErrorSubject.next(this.isUnavailable(error) ? 'No accessible workspace in this organization. Ask an organization manager for access.' : 'Workspace could not be loaded. Try again.');
                return;
            }
        }
        if (current()) this.selectionErrorSubject.next('No accessible workspace in this organization. Ask an organization manager for access.');
    }
    public onWorkspaceCreated(workspace: IWSInDTO, oran: IOrganIn) {
        if (!oran || oran.id !== this.currentOrganId) return;
        ++this.selectionSequence;
        const localWs = this.convertAndSetCurrentWS(workspace);
        this._newWorkspaceCreatedSubject.next(localWs);
        this.organizationStateManager.onWorkspacesUpdate(oran);
    }

    public onWorkspaceUpdated(workspace: IWSInDTO, action: WSUpdateAction) {
        const localWs = this.convertAndSetIfCurrent(workspace);
        this.organizationStateManager.onWSMetadataUpdate(localWs, action);
    }

    public onWorkspaceAssociationUpdate(wsId: string, type: WSUpdateAssocEnum, action: WSAssocUpdateActionType) {
        this.organizationStateManager.onWSAssociationUpdate(wsId, type, action);
    }

    public onWorkspaceBrandingUpdated(branding: IBranding, wsId: string) {
        const workspaceData = this._currentWorkspaceSubject.value;
        if (!workspaceData) {
            return;
        }
        const { workspace } = workspaceData;
        if (!workspace) {
            return;
        }
        if (workspace.id !== wsId) {
            return;
        }
        workspace.branding = branding;
        const localWs = this.convertAndSetIfCurrent(workspace);
    }

    public getCurrentWorkspaceAsync() {
        return this.functionsHelper.getDataAsync(this._currentWorkspaceSubject);
    }

    private convertAndSetIfCurrent(inWS: IWSInDTO) {
        const currentWsData = this._currentWorkspaceSubject.value;
        const currentWS = currentWsData?.workspace;
        if (!currentWS || currentWS.id !== inWS?.id) {
            return;
        }
        return this.convertAndSetCurrentWS(inWS);
    }

    private convertAndSetCurrentWS(inWS: IWSInDTO) {
        const localWs = this.workspaceConverter.inToLocal(inWS);
        this.setCurrentWorkspace(localWs);
        return localWs;
    }

    private async getWorkspaceAsync(wsId: string) {
        return this.workspaceApiManagerService.getWorkspaceMetadataAsync(wsId);
    }

    private setCurrentWorkspace(ws: IWorkspace) {
        if (!ws) {
            throw new MissingArgumentsError(`Could not set current workspace because it's null!`);
        }
        const wsData = this._currentWorkspaceSubject.value;
        const currentWs = wsData.workspace;
        let isSwapped: boolean = false;
        if (currentWs?.id !== ws.id) {
            if (currentWs) {
                this.snackBarService.openMessage(`You're now in ${ws.name} workspace.`, null, 4000, {
                    panelClass: 'snackbar',
                });
            }
            isSwapped = true;
        }
        this.workspaceApiManagerService.setWsContext({
            organizationId: this.currentOrganId,
            workspaceId: ws.id,
        });
        this._currentWorkspaceIdSubject.next(ws.id);
        this._currentWorkspaceSubject.next({ workspace: ws, isSwapped: isSwapped });
        this._isMasterBrandingSubject.next(ws.isMaster);
        this.setWorkspaceIdInLS(ws.id);
        this.selectionErrorSubject.next('');
    }

    private clearWorkspace() {
        ++this.selectionSequence;
        this.selectionErrorSubject.next('');
        this.clearSelectedWorkspace();
    }
    private clearSelectedWorkspace() {
        this.workspaceApiManagerService.setOrgContext({ organizationId: this.currentOrganId });
        this.workspaceApiManagerService.setWsContext({ organizationId: this.currentOrganId, workspaceId: null });
        this._currentWorkspaceIdSubject.next(null);
        this._currentWorkspaceSubject.next({ workspace: null, isSwapped: true });
        this._isMasterBrandingSubject.next(false);
    }

    private getWorkspaceIdFromLS() {
        return localStorage.getItem(this.WORKSPACE_LS_ID);
    }

    private setWorkspaceIdInLS(id: string) {
        localStorage.setItem(this.WORKSPACE_LS_ID, id);
    }

    private async updateOrganWSDefaultIdsAsync() {
        const { data, error } = await this.workspaceApiManagerService.updateWSOrganDefaultIdsAsync();
        if (error) {
            console.error(`Could not update workspace name! Error:`, error);
            return;
        }
    }
}
